import { useSelector } from 'store/hooks/useSelector'
import { type UseParticipantConnectionStatusesReturnType } from '../../useParticipantConnectionStatuses'
import { useCallIsActive } from '../../useCallIsActive'
import { usePreviousValueAutoUpdatingRef } from '../../usePreviousValueAutoUpdatingRef'
import { useEffect } from 'react'
import { useMeetingState } from '@daily-co/daily-react'
import { usePrevious } from '@quickmd-llc/frontend-utils'
import { type VisitLifecycleState } from './useCurrentState'
import { useDailyVisitInfo } from '../../useDailyVisitInfo'
import { useProviderConnected } from '../../useProviderConnected'
import { usePatientConnected } from '../../usePatientConnected'

// Actions are one-time events that happen at a point in time
export type VisitLifecycleAction =
  | 'PERMISSIONS_GRANTED'
  | 'PERMISSIONS_DENIED'
  | 'REQUESTING_NEW_ROOM_INFO'
  | 'ROOM_INFO_RECEIVED'
  | 'DAILY_CONNECTING'
  | 'PROVIDER_CONNECTED_AND_CALLING'
  | 'PATIENT_CONNECTED'
  | 'CALL_IS_ACTIVE'
  | 'SERVICE_INTERRUPTED'
  | 'SERVICE_RESUMED'
  | 'PATIENT_DECLINED'
  | 'CALL_TIMED_OUT'
  | 'ERROR'
  | 'PATIENT_DISCONNECTED'
  | 'STATE_TRANSITION'

export type ActionHistoryEntry = {
  type: VisitLifecycleAction | 'STATE_TRANSITION'
  from?: VisitLifecycleState
  to?: VisitLifecycleState
  timestamp: number
}

type Input = {
  recordAction: (action: VisitLifecycleAction) => void
  currentState: VisitLifecycleState
  lastJoinAttemptDidConnectWithPatient: boolean
  callTimedOut: boolean
} & UseParticipantConnectionStatusesReturnType

/**
 * *NOTE: all of this is solely for dev purposes. the action tracking is just to see in the dev tools what has happened in the visit lifecycle.
 */
export function useTrackActionHistory(input: Input) {
  const {
    participantConnectionStatuses,
    recordAction,
    callTimedOut,
    currentState,
    lastJoinAttemptDidConnectWithPatient,
  } = input

  const { providerConnected } = useProviderConnected()

  const { patientConnected } = usePatientConnected()

  const meetingState = useMeetingState()
  const { isVideoDeclined, permissionsGranted, permissionsDenied, isRequestingRoomInfo } = useSelector((state) => {
    const wr = state.waitingRoomReducer
    return {
      isVideoDeclined: wr.isVideoDeclined,
      permissionsGranted: wr.permissionsGranted,
      permissionsDenied: wr.permissionsDenied,
      isRequestingRoomInfo: wr.isRequestingRoomInfo,
    }
  })

  const dailyVisitInfo = useDailyVisitInfo()

  const { callIsActive } = useCallIsActive()

  const permissionsGrantedPreviousRef = usePreviousValueAutoUpdatingRef(permissionsGranted)

  // PERMISSIONS_GRANTED
  useEffect(() => {
    const isNotEffectInitialization = permissionsGrantedPreviousRef.current !== undefined
    if (permissionsGranted && isNotEffectInitialization) {
      recordAction('PERMISSIONS_GRANTED')
    }
  }, [permissionsGranted, permissionsGrantedPreviousRef, recordAction])

  // PERMISSIONS_DENIED
  useEffect(() => {
    const isNotEffectInitialization = permissionsGrantedPreviousRef.current !== undefined
    if (permissionsDenied && isNotEffectInitialization) {
      recordAction('PERMISSIONS_DENIED')
    }
  }, [permissionsDenied, permissionsGrantedPreviousRef, recordAction])

  // REQUESTING_NEW_ROOM_INFO
  useEffect(() => {
    if (isRequestingRoomInfo) {
      recordAction('REQUESTING_NEW_ROOM_INFO')
    }
  }, [isRequestingRoomInfo, recordAction])

  const dailyVisitInfoPreviousRef = usePreviousValueAutoUpdatingRef(dailyVisitInfo)

  // ROOM_INFO_RECEIVED
  useEffect(() => {
    const isNotEffectInitialization = dailyVisitInfoPreviousRef.current !== undefined
    if (
      isNotEffectInitialization &&
      dailyVisitInfo?.url &&
      dailyVisitInfo?.url !== dailyVisitInfoPreviousRef.current?.url
    ) {
      recordAction('ROOM_INFO_RECEIVED')
    }
  }, [dailyVisitInfo, dailyVisitInfoPreviousRef, recordAction])

  // DAILY_CONNECTING - when provider starts connecting to Daily
  useEffect(() => {
    const isConnecting = meetingState === 'joining-meeting' || meetingState === 'loading'
    if (isConnecting) {
      recordAction('DAILY_CONNECTING')
    }
  }, [meetingState, recordAction])

  // PROVIDER_CONNECTED_AND_CALLING - when provider connects
  useEffect(() => {
    if (providerConnected) {
      recordAction('PROVIDER_CONNECTED_AND_CALLING')
    }
  }, [providerConnected, recordAction])

  // PATIENT_CONNECTED - when patient connects
  useEffect(() => {
    if (patientConnected && providerConnected) {
      recordAction('PATIENT_CONNECTED')
    }
  }, [patientConnected, providerConnected, recordAction])

  // CALL_IS_ACTIVE - when call is active
  useEffect(() => {
    if (callIsActive) {
      recordAction('CALL_IS_ACTIVE')
    }
  }, [callIsActive, recordAction])

  // PATIENT_DECLINED
  useEffect(() => {
    if (isVideoDeclined) {
      recordAction('PATIENT_DECLINED')
    }
  }, [isVideoDeclined, recordAction])

  // CALL_TIMED_OUT
  useEffect(() => {
    if (callTimedOut) {
      recordAction('CALL_TIMED_OUT')
    }
  }, [callTimedOut, recordAction])

  // PATIENT_DISCONNECTED
  useEffect(() => {
    if (lastJoinAttemptDidConnectWithPatient && !patientConnected && providerConnected) {
      recordAction('PATIENT_DISCONNECTED')
    }
  }, [lastJoinAttemptDidConnectWithPatient, patientConnected, providerConnected, recordAction])

  // ERROR - when meeting state is error
  useEffect(() => {
    if (meetingState === 'error') {
      recordAction('ERROR')
    }
  }, [meetingState, recordAction])

  const providerConnectionStatus = participantConnectionStatuses.provider

  const previousProviderConnectionStatus = usePrevious(participantConnectionStatuses.provider)

  // SERVICE_INTERRUPTED and SERVICE_RESUMED based on providerConnectionStatus
  useEffect(() => {
    if (
      providerConnected &&
      (callIsActive ||
        currentState === 'PROVIDER_CONNECTED_AND_WAITING_FOR_PATIENT_CONNECTION' ||
        currentState === 'CALL_IS_ACTIVE')
    ) {
      if (
        (providerConnectionStatus === 'disconnected' || providerConnectionStatus === 'bad') &&
        previousProviderConnectionStatus !== providerConnectionStatus
      ) {
        recordAction('SERVICE_INTERRUPTED')
      }
      if (
        providerConnectionStatus === 'good' &&
        (previousProviderConnectionStatus === 'disconnected' || previousProviderConnectionStatus === 'bad')
      ) {
        recordAction('SERVICE_RESUMED')
      }
    }
  }, [
    providerConnectionStatus,
    previousProviderConnectionStatus,
    providerConnected,
    callIsActive,
    currentState,
    recordAction,
  ])
}
